import { Container, Graphics, Sprite, Texture } from 'pixi.js-legacy';
import type { BoardState, Tile } from '../game/types';
import { BOARD_WIDTH, BOARD_HEIGHT } from '../game/types';
import { HIGHLIGHT_COLOR } from './colors';
import { ParticleAnimator } from './particle-animator';
import { TileAnimator } from './tile-animator';

export const TILE_SIZE = 47;
export const TILE_PADDING = 2;
export const GRID_OFFSET_X = 20;
export const GRID_OFFSET_Y = 80;

/**
 * Check if winter theme is enabled via URL parameter
 */
function isWinterThemeEnabled(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('winter') === '1';
}

/**
 * Get tile texture paths based on theme
 */
function getTileTextures(): string[] {
  const isWinter = isWinterThemeEnabled();
  const suffix = isWinter ? '_winter' : '';
  
  return [
    `./images/tile1${suffix}.png`, // Color 0 - Blue
    `./images/tile2${suffix}.png`, // Color 1 - Orange
    `./images/tile3${suffix}.png`, // Color 2 - Green
    `./images/tile4${suffix}.png`, // Color 3 - Purple
    `./images/tile5${suffix}.png`, // Color 4 - Yellow
  ];
}

// Tile texture mapping: color index to image path
const TILE_TEXTURES = getTileTextures();

/**
 * Renderer for the game board grid
 */
export class GridRenderer {
  private container: Container;
  private tiles: Sprite[][] = [];
  private highlightGraphics: Graphics;
  private particleAnimator: ParticleAnimator;
  private tileAnimator: TileAnimator;

  constructor() {
    this.container = new Container();
    this.container.eventMode = 'static';
    this.container.hitArea = {
      contains: (x: number, y: number) => {
        return (
          x >= GRID_OFFSET_X &&
          x <= GRID_OFFSET_X + BOARD_WIDTH * (TILE_SIZE + TILE_PADDING) &&
          y >= GRID_OFFSET_Y &&
          y <= GRID_OFFSET_Y + BOARD_HEIGHT * (TILE_SIZE + TILE_PADDING)
        );
      },
    };
    this.highlightGraphics = new Graphics();
    this.container.addChild(this.highlightGraphics);

    // Initialize animators
    this.particleAnimator = new ParticleAnimator(this.container);
    this.tileAnimator = new TileAnimator();

    // Create tile sprites
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      const row: Sprite[] = [];
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const tileSprite = new Sprite();
        tileSprite.x = GRID_OFFSET_X + x * (TILE_SIZE + TILE_PADDING);
        tileSprite.y = GRID_OFFSET_Y + y * (TILE_SIZE + TILE_PADDING);
        // Scale from 64px to TILE_SIZE (47px)
        const scale = TILE_SIZE / 64;
        tileSprite.scale.set(scale, scale);
        this.container.addChild(tileSprite);
        row.push(tileSprite);
      }
      this.tiles.push(row);
    }
  }

  /**
   * Get the container for adding to stage
   */
  getContainer(): Container {
    return this.container;
  }

  /**
   * Render the board state
   */
  render(board: BoardState): void {
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const tile = board.grid[y][x];
        const sprite = this.tiles[y][x];

        if (tile.alive) {
          this.drawTile(sprite, tile);
          sprite.visible = true;
        } else {
          sprite.visible = false;
        }
      }
    }
  }

  /**
   * Draw a single tile
   */
  private drawTile(sprite: Sprite, tile: Tile): void {
    // Set the texture based on tile color
    const texturePath = TILE_TEXTURES[tile.color];
    sprite.texture = Texture.from(texturePath);
  }

  /**
   * Highlight a group of tiles
   */
  highlightGroup(group: Array<{ x: number; y: number }>): void {
    this.highlightGraphics.clear();

    if (group.length < 2) return; // Only highlight groups of 2+

    // Draw subtle highlight overlay
    this.highlightGraphics.lineStyle(2, HIGHLIGHT_COLOR, 0.6);
    for (const pos of group) {
      const screenX = GRID_OFFSET_X + pos.x * (TILE_SIZE + TILE_PADDING);
      const screenY = GRID_OFFSET_Y + pos.y * (TILE_SIZE + TILE_PADDING);

      this.highlightGraphics.drawRect(screenX, screenY, TILE_SIZE, TILE_SIZE);
    }
  }

  /**
   * Clear highlight
   */
  clearHighlight(): void {
    this.highlightGraphics.clear();
  }

  /**
   * Convert screen position to grid coordinates
   */
  screenToGrid(x: number, y: number): { x: number; y: number } | null {
    const gridX = Math.floor((x - GRID_OFFSET_X) / (TILE_SIZE + TILE_PADDING));
    const gridY = Math.floor((y - GRID_OFFSET_Y) / (TILE_SIZE + TILE_PADDING));

    if (
      gridX >= 0 &&
      gridX < BOARD_WIDTH &&
      gridY >= 0 &&
      gridY < BOARD_HEIGHT
    ) {
      return { x: gridX, y: gridY };
    }

    return null;
  }

  /**
   * Trigger crumble animation for removed tiles and animate remaining tiles
   */
  animateTileRemoval(
    positions: Array<{ x: number; y: number }>,
    boardBeforeGravity: BoardState,
    boardAfterGravity: BoardState
  ): void {
    // Group positions by color to animate with correct texture
    const colorGroups = new Map<number, Array<{ x: number; y: number }>>();

    for (const pos of positions) {
      const tile = boardBeforeGravity.grid[pos.y][pos.x];
      if (!colorGroups.has(tile.color)) {
        colorGroups.set(tile.color, []);
      }
      colorGroups.get(tile.color)!.push(pos);
    }

    // Create crumble animations for each color group
    for (const [color, groupPositions] of colorGroups) {
      const texturePath = TILE_TEXTURES[color];
      this.particleAnimator.createCrumbleEffect(groupPositions, texturePath);
    }

    // Animate remaining tiles to their new positions
    this.animateTileMovement(boardBeforeGravity, boardAfterGravity);
  }

  /**
   * Animate tiles smoothly to their new positions after gravity
   */
  private animateTileMovement(
    boardBefore: BoardState,
    boardAfter: BoardState
  ): void {
    // Build a map of tile identities (color + position) to track movement
    for (let newY = 0; newY < BOARD_HEIGHT; newY++) {
      for (let newX = 0; newX < BOARD_WIDTH; newX++) {
        const newTile = boardAfter.grid[newY][newX];
        if (!newTile.alive) continue;

        // Find where this tile came from
        const oldPos = this.findTileOrigin(
          newTile,
          newX,
          newY,
          boardBefore,
          boardAfter
        );

        if (oldPos && (oldPos.x !== newX || oldPos.y !== newY)) {
          // Calculate screen positions
          const startX = GRID_OFFSET_X + oldPos.x * (TILE_SIZE + TILE_PADDING);
          const startY = GRID_OFFSET_Y + oldPos.y * (TILE_SIZE + TILE_PADDING);
          const targetX = GRID_OFFSET_X + newX * (TILE_SIZE + TILE_PADDING);
          const targetY = GRID_OFFSET_Y + newY * (TILE_SIZE + TILE_PADDING);

          // Animate the sprite
          const sprite = this.tiles[newY][newX];
          sprite.x = startX;
          sprite.y = startY;
          this.tileAnimator.animateTile(
            sprite,
            startX,
            startY,
            targetX,
            targetY
          );
        }
      }
    }
  }

  /**
   * Find where a tile came from before gravity was applied
   */
  private findTileOrigin(
    tile: Tile,
    newX: number,
    newY: number,
    boardBefore: BoardState,
    _boardAfter: BoardState
  ): { x: number; y: number } | null {
    // For simplicity, we look upward in the same column for matching tiles
    // This works because gravity only moves tiles down/left

    // First check same column above
    for (let oldY = 0; oldY <= newY; oldY++) {
      const oldTile = boardBefore.grid[oldY][newX];
      if (oldTile.alive && oldTile.color === tile.color) {
        // Check if this tile hasn't been matched yet
        if (!boardBefore.grid[oldY][newX].matched) {
          boardBefore.grid[oldY][newX].matched = true;
          return { x: newX, y: oldY };
        }
      }
    }

    // If not found in same column, check columns to the right (compaction)
    for (let oldX = newX + 1; oldX < BOARD_WIDTH; oldX++) {
      for (let oldY = 0; oldY < BOARD_HEIGHT; oldY++) {
        const oldTile = boardBefore.grid[oldY][oldX];
        if (oldTile.alive && oldTile.color === tile.color) {
          if (!boardBefore.grid[oldY][oldX].matched) {
            boardBefore.grid[oldY][oldX].matched = true;
            return { x: oldX, y: oldY };
          }
        }
      }
    }

    return null;
  }

  /**
   * Update animations
   */
  updateAnimations(deltaTime: number): void {
    this.particleAnimator.update(deltaTime);
    this.tileAnimator.update(deltaTime);
  }

  /**
   * Check if animations are running
   */
  isAnimating(): boolean {
    return this.particleAnimator.isRunning() || this.tileAnimator.isRunning();
  }

  /**
   * Clear all animations (for restart/undo)
   */
  clearAnimations(): void {
    this.particleAnimator.clear();
    this.tileAnimator.clear();
  }
}
